<template>
  <v-card
    class="mx-auto"
    width="100%"
  >
    <v-card-title>
      <v-app-bar
        flat
        class="white pa-0"
        dense
      >
        <v-toolbar-title data-cy="transactions_title">
          {{ $t('transaction.title', 'Transaction Monitoring') }}
        </v-toolbar-title>
        <v-spacer />
        <v-btn
          color="primary"
          outlined
          class="mr-2"
          @click="fetchTransactions"
        >
          <v-icon left>
            mdi-refresh
          </v-icon> Reload
        </v-btn>
      </v-app-bar>
    </v-card-title>

    <v-container
      fluid
      class="pt-0"
    >
      <!-- Filter Bar -->
      <v-card
        flat
        class="pa-3 mb-4 grey lighten-4"
      >
        <v-row dense>
          <v-col
            cols="12"
            sm="2"
          >
            <v-text-field
              v-model="filters.partyId"
              label="Party ID"
              dense
              outlined
              clearable
              hide-details
              :disabled="!!partyId"
            />
          </v-col>
          <v-col
            cols="12"
            sm="2"
          >
            <v-text-field
              v-model="filters.transactionId"
              label="Transaction ID"
              dense
              outlined
              clearable
              hide-details
            />
          </v-col>
          <v-col
            cols="12"
            sm="2"
          >
            <v-text-field
              v-model="filters.accountId"
              label="Account ID"
              dense
              outlined
              clearable
              hide-details
            />
          </v-col>
          <v-col
            cols="12"
            sm="2"
          >
            <v-select
              v-model="filters.sector"
              :items="sectorOptions"
              item-text="label"
              item-value="value"
              label="Sector"
              dense
              outlined
              clearable
              hide-details
            />
          </v-col>
          <v-col
            cols="12"
            sm="2"
          >
            <v-select
              v-model="filters.channel"
              :items="channelOptions"
              item-text="label"
              item-value="value"
              label="Channel"
              dense
              outlined
              clearable
              hide-details
            />
          </v-col>
          <v-col
            cols="12"
            sm="2"
          >
            <v-select
              v-model="filters.status"
              :items="statusOptions"
              item-text="label"
              item-value="value"
              label="Status"
              dense
              outlined
              clearable
              hide-details
            />
          </v-col>
          <v-col
            cols="12"
            sm="2"
          >
            <v-select
              v-model="filters.direction"
              :items="directionOptions"
              item-text="label"
              item-value="value"
              label="Direction"
              dense
              outlined
              clearable
              hide-details
            />
          </v-col>
          <v-col
            cols="12"
            sm="2"
          >
            <v-select
              v-model="filters.assetClass"
              :items="['EQUITY_BLUE_CHIP', 'EQUITY_PENNY', 'OPTIONS', 'MUTUAL_FUND', 'CRYPTO']"
              label="Asset Class"
              dense
              outlined
              clearable
              hide-details
            />
          </v-col>
          <v-col
            cols="12"
            sm="2"
          >
            <v-text-field
              v-model.number="filters.minAmountHKD"
              label="Min Amount (HKD)"
              type="number"
              dense
              outlined
              clearable
              hide-details
            />
          </v-col>
          <v-col
            cols="12"
            sm="2"
          >
            <v-text-field
              v-model.number="filters.maxAmountHKD"
              label="Max Amount (HKD)"
              type="number"
              dense
              outlined
              clearable
              hide-details
            />
          </v-col>
          <v-col
            cols="12"
            sm="4"
            class="d-flex align-center"
          >
            <v-btn
              color="primary"
              class="mr-2"
              height="40"
              @click="onSearch"
            >
              Filter
            </v-btn>
            <v-btn
              color="grey lighten-1"
              dark
              height="40"
              @click="onReset"
            >
              Reset
            </v-btn>
          </v-col>
        </v-row>
      </v-card>

      <!-- Transactions Table -->
      <v-data-table
        :headers="headers"
        :items="transactions"
        :loading="isLoading"
        :options.sync="options"
        :server-items-length="totalCount"
        class="elevation-1"
      >
        <template v-slot:item.transactionTimestamp="{ item }">
          {{ formatDate(item.transactionTimestamp) }}
        </template>

        <template v-slot:item.amount="{ item }">
          {{ item.currency || 'HKD' }} {{ formatAmount(item.amount) }}
          <span
            v-if="item.amountInHKD"
            class="caption grey--text"
          >
            (${{ formatAmount(item.amountInHKD) }} HKD)
          </span>
        </template>

        <template v-slot:item.status="{ item }">
          <v-chip
            :color="getStatusColor(item.status)"
            dark
            small
          >
            {{ item.status }}
          </v-chip>
        </template>

        <template v-slot:item.actions="{ item }">
          <v-btn
            icon
            small
            color="primary"
            @click="openDetails(item.transactionId)"
          >
            <v-icon>mdi-eye</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-container>

    <!-- Details Modal -->
    <v-dialog
      v-model="detailsDialog"
      width="750px"
    >
      <v-card v-if="selectedTransaction">
        <v-card-title class="headline grey lighten-2">
          Transaction Details: {{ selectedTransaction.transactionId }}
        </v-card-title>
        <v-card-text class="pt-4">
          <v-row dense>
            <v-col cols="6">
              <strong>Transaction ID:</strong> {{ selectedTransaction.transactionId }}
            </v-col>
            <v-col cols="6">
              <strong>Party ID:</strong> {{ selectedTransaction.partyId }} {{ selectedTransaction.partyType ? `(${selectedTransaction.partyType})` : '' }}
            </v-col>
            <v-col cols="6">
              <strong>Account ID:</strong> {{ selectedTransaction.accountId }}
            </v-col>
            <v-col cols="6">
              <strong>Account Opening Date:</strong> {{ formatDate(selectedTransaction.accountOpeningTimestamp) }}
            </v-col>
            <v-col cols="6">
              <strong>Direction:</strong> {{ selectedTransaction.direction }}
            </v-col>
            <v-col cols="6">
              <strong>Channel:</strong> {{ selectedTransaction.channel }}
            </v-col>
            <v-col cols="6">
              <strong>Status:</strong> {{ selectedTransaction.status }}
            </v-col>
            <v-col cols="6">
              <strong>Amount:</strong> {{ selectedTransaction.currency }} {{ formatAmount(selectedTransaction.amount) }}
            </v-col>
            <v-col cols="6">
              <strong>Amount (HKD):</strong> ${{ formatAmount(selectedTransaction.amountInHKD) }}
            </v-col>
            <v-col cols="6">
              <strong>Timestamp:</strong> {{ formatDate(selectedTransaction.transactionTimestamp) }}
            </v-col>
          </v-row>

          <!-- Counterparty Details -->
          <v-divider class="my-3" />
          <div class="subtitle-1 font-weight-bold mb-2">
            Counterparty Context
          </div>
          <v-row
            dense
            v-if="selectedTransaction.counterparty"
          >
            <v-col cols="6">
              <strong>Name:</strong> {{ selectedTransaction.counterparty.name }}
            </v-col>
            <v-col cols="6">
              <strong>Account Number:</strong> {{ selectedTransaction.counterparty.accountNumber }}
            </v-col>
            <v-col cols="6">
              <strong>Bank:</strong> {{ selectedTransaction.counterparty.bankName || 'N/A' }}
            </v-col>
            <v-col cols="6">
              <strong>Routing Code:</strong> {{ selectedTransaction.counterparty.routingCode || 'N/A' }}
            </v-col>
            <v-col cols="6">
              <strong>Country:</strong> {{ selectedTransaction.counterparty.country }}
            </v-col>
          </v-row>
          <v-row
            dense
            v-else
          >
            <v-col
              cols="12"
              class="grey--text"
            >
              No counterparty information available.
            </v-col>
          </v-row>

          <!-- Device Metadata Details -->
          <v-divider
            class="my-3"
            v-if="selectedTransaction.metadata"
          />
          <div
            class="subtitle-1 font-weight-bold mb-2"
            v-if="selectedTransaction.metadata"
          >
            Metadata Analytics
          </div>
          <v-row
            dense
            v-if="selectedTransaction.metadata"
          >
            <v-col cols="4">
              <strong>IP Address:</strong> {{ selectedTransaction.metadata.ipAddress || 'N/A' }}
            </v-col>
            <v-col cols="4">
              <strong>Device ID:</strong> {{ selectedTransaction.metadata.deviceId || 'N/A' }}
            </v-col>
            <v-col cols="4">
              <strong>Location:</strong> {{ selectedTransaction.metadata.locationCoordinates || 'N/A' }}
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="detailsDialog = false"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import moment from 'moment'

export default {
  name: 'TMTransaction',
  props: {
    partyId: {
      type: String,
      default: null
    }
  },
  data () {
    return {
      detailsDialog: false,
      options: { page: 1, itemsPerPage: 20, sortBy: ['transactionTimestamp'], sortDesc: [true] },
      filters: {
        partyId: this.partyId || '',
        transactionId: '',
        accountId: '',
        sector: null,
        channel: null,
        status: null,
        direction: 'ANY',
        assetClass: null,
        minAmountHKD: null,
        maxAmountHKD: null
      },
      headers: [
        { text: 'Transaction ID', value: 'transactionId' },
        { text: 'Party ID', value: 'partyId' },
        { text: 'Account ID', value: 'accountId' },
        { text: 'Date', value: 'transactionTimestamp' },
        { text: 'Channel', value: 'channel' },
        { text: 'Amount', value: 'amount' },
        { text: 'Status', value: 'status' },
        { text: 'Actions', value: 'actions', sortable: false }
      ]
    }
  },
  computed: {
    // Note: Ensure 'tm_transaction' matches your key in store/index.js
    ...mapGetters('tm_transaction', [
      'transactions',
      'totalCount',
      'selectedTransaction',
      'isLoading',
      'channelOptions',
      'statusOptions',
      'directionOptions',
      'sectorOptions'
    ])
  },
  watch: {
    options: {
      handler () {
        this.applyAndFetch()
      },
      deep: true
    },
    partyId: {
      immediate: true,
      handler (newVal) {
        if (newVal) {
          this.filters.partyId = newVal
          this.applyAndFetch()
        }
      }
    }
  },
  methods: {
    ...mapActions('tm_transaction', [
      'fetchTransactions',
      'fetchTransactionById',
      'setFilters',
      'resetFilters'
    ]),

    // Updates store filters and automatically triggers fetchTransactions
    async applyAndFetch () {
      const payload = {
        page: this.options.page || 1,
        limit: this.options.itemsPerPage || 20,
        sortBy: this.options.sortBy?.[0] || 'transactionTimestamp',
        sortOrder: this.options.sortDesc?.[0] ? 'desc' : 'asc',
        ...this.filters
      }
      await this.setFilters(payload)
    },

    onSearch () {
      this.options.page = 1
      this.applyAndFetch()
    },

    async onReset () {
      this.filters = {
        partyId: this.partyId || '',
        transactionId: '',
        accountId: '',
        sector: null,
        channel: null,
        status: null,
        direction: 'ANY',
        assetClass: null,
        minAmountHKD: null,
        maxAmountHKD: null
      }
      this.options.page = 1
      // resetFilters in store resets state and dispatches fetchTransactions automatically
      await this.resetFilters()
      if (this.partyId) {
        await this.setFilters({ partyId: this.partyId })
      }
    },

    async openDetails (id) {
      try {
        await this.fetchTransactionById(id)
        this.detailsDialog = true
      } catch (error) {
        console.error('Failed to load transaction details:', error)
      }
    },

    getStatusColor (status) {
      if (status === 'SETTLED' || status === 'COMPLETED') return 'green'
      if (status === 'PENDING') return 'orange'
      return 'red'
    },

    formatDate (value) {
      if (!value) return '-'
      const ts = typeof value === 'string' ? parseInt(value, 10) : value
      const formattedTs = ts < 10000000000 ? ts * 1000 : ts
      return moment(formattedTs).format('DD/MMM/YYYY HH:mm:ss')
    },

    formatAmount (value) {
      return value != null ? Number(value).toLocaleString(undefined, { minimumFractionDigits: 2 }) : '0.00'
    }
  }
}
</script>
